import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiIncrementalityControlRegionPayload,
  ApiMetric,
} from "@incendium/api";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { IChart, TChartData } from "Interfaces";
import { useSelectedProject } from "Hooks";
import { useChartData } from "features/analytics";
import { useEffect, useMemo } from "react";
import { useIncrementalitySetupContext } from "../providers/IncrementalitySetupProvider";
import produce from "immer";

export interface IncrementalityRegionOption extends ApiIncrementalityControlRegionPayload {
  label: string;
  sessions: number;
}

const REGIONS_CHART: IChart = {
  name: "Incrementality Top Cities",
  dimension: [
    ApiDimension.DIMENSION_LAT_LONG,
    ApiDimension.DIMENSION_COUNTRY,
    ApiDimension.DIMENSION_CITY,
  ],
  template: ApiChartTemplate.TABLE,
  type: ApiChartType.TABLE,
  attributes: [],
  limit: 100,
  orderBy: `${ApiMetric.METRIC_SESSIONS_COUNT} DESC`,
  yAxisKeys: [
    {
      key: "l",
      fields: [ApiMetric.METRIC_SESSIONS_COUNT],
    },
  ],
  displayOptions: {
    noFilters: true,
  },
};

const DATE_OVERRIDE = {
  from: undefined,
  to: undefined,
  lastNDays: 90,
};

function parseLatLon(row: TChartData): { lat: number; lon: number } | null {
  const raw = String(row.name || "");
  const [lonRaw, latRaw] = raw.split(",");
  const lon = Number(lonRaw);
  const lat = Number(latRaw);
  if (Number.isNaN(lat) || Number.isNaN(lon)) {
    return null;
  }
  return { lat, lon };
}

function IncrementalityTestRegionAutocomplete() {
  const { incrementalityTest, setIncrementalityTest } =
    useIncrementalitySetupContext();
  const { selectedProject } = useSelectedProject();
  const { chartData, loading } = useChartData(
    selectedProject,
    REGIONS_CHART,
    DATE_OVERRIDE,
    { ignoreModel: true },
  );

  const options = useMemo<IncrementalityRegionOption[]>(() => {
    return (chartData?.data || [])
      .map<IncrementalityRegionOption | null>((row) => {
        const coords = parseLatLon(row);
        if (!coords) return null;
        const city = String(row[ApiDimension.DIMENSION_CITY] || "").trim();
        const country = String(
          row[ApiDimension.DIMENSION_COUNTRY] || "",
        ).trim();
        if (!city || !country) return null;
        const sessions = Number(row[ApiMetric.METRIC_SESSIONS_COUNT] || 0);
        return {
          id: `${city}-${country}-${coords.lat}-${coords.lon}`,
          city,
          country,
          label: `${city}, ${country}`,
          sessions,
          lat: coords.lat,
          lon: coords.lon,
        };
      })
      .filter((item): item is IncrementalityRegionOption => !!item)
      .sort((a, b) => b.sessions - a.sessions);
  }, [chartData?.data]);

  const selectedRegion = useMemo<IncrementalityRegionOption | null>(() => {
    if (
      !incrementalityTest.testRegionCity ||
      !incrementalityTest.testRegionCountry
    ) {
      return null;
    }

    const fromOptions =
      options.find(
        (option) =>
          option.city === incrementalityTest.testRegionCity &&
          option.country === incrementalityTest.testRegionCountry &&
          option.lat === (incrementalityTest.testRegionLat || 0) &&
          option.lon === (incrementalityTest.testRegionLon || 0),
      ) || null;
    if (fromOptions) return fromOptions;

    return {
      id: `${incrementalityTest.testRegionCity}-${incrementalityTest.testRegionCountry}-${incrementalityTest.testRegionLat || 0}-${incrementalityTest.testRegionLon || 0}`,
      city: incrementalityTest.testRegionCity,
      country: incrementalityTest.testRegionCountry,
      label: `${incrementalityTest.testRegionCity}, ${incrementalityTest.testRegionCountry}`,
      sessions: 0,
      lat: incrementalityTest.testRegionLat || 0,
      lon: incrementalityTest.testRegionLon || 0,
    };
  }, [
    options,
    incrementalityTest.testRegionCity,
    incrementalityTest.testRegionCountry,
    incrementalityTest.testRegionLat,
    incrementalityTest.testRegionLon,
  ]);

  useEffect(() => {
    if (
      !incrementalityTest.testRegionCity &&
      !incrementalityTest.testRegionCountry &&
      options.length > 0
    ) {
      const first = options[0];
      setIncrementalityTest((prev) =>
        produce(prev, (draft) => {
          draft.testRegionCity = first.city;
          draft.testRegionCountry = first.country;
          draft.testRegionLat = first.lat;
          draft.testRegionLon = first.lon;
        }),
      );
    }
  }, [
    incrementalityTest.testRegionCity,
    incrementalityTest.testRegionCountry,
    options,
    setIncrementalityTest,
  ]);

  return (
    <Autocomplete
      fullWidth
      options={options}
      value={selectedRegion}
      loading={loading}
      onChange={(_, next) =>
        setIncrementalityTest((prev) =>
          produce(prev, (draft) => {
            draft.testRegionCity = next?.city;
            draft.testRegionCountry = next?.country;
            draft.testRegionLat = next?.lat;
            draft.testRegionLon = next?.lon;
          }),
        )
      }
      isOptionEqualToValue={(option, selected) =>
        option.city === selected.city &&
        option.country === selected.country &&
        option.lat === selected.lat &&
        option.lon === selected.lon
      }
      getOptionLabel={(option) => option.label}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Test region"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default IncrementalityTestRegionAutocomplete;
